import { z } from 'zod';

// ── Shared field schemas ──────────────────────────────────

const phoneRegExp = /^\+?([0-9]{1,4})?[ ]?(\(?\d{1,4}\)?)[ ]?(\d{1,4})[ ]?(\d{1,4})[ ]?(\d{1,9})$/;
const AUS_POSTCODE_REGEX = /^\d{4}$/;
const AUS_ACN = /^\d(?:\s?\d){8}$/;
const AUS_ABN = /^\d(?:\s?\d){10}$/;

const emailField = z.string().trim().email('Please enter a valid email address');
const phoneField = z.string().trim().regex(phoneRegExp, 'Please enter a valid phone number');
const monetaryAmount = z.coerce.number().positive('Must be a positive number');
const specialCharRegex = new RegExp(`[!@#$%^&*()_+=\\-\\[\\]{};':"\\\\|,.<>/?]`);
const optionalMonetary = z.preprocess(
  val => (val === '' || val === undefined || val === null ? undefined : val),
  z.coerce.number().nonnegative('Cannot be negative').optional(),
);
const dateOnlyField = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format');

function emptyToUndefined(value: unknown) {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function normalizeDigitsAndSpaces(value: unknown) {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed.replace(/\s+/g, ' ');
}

function optionalTrimmedString(max: number, message?: string) {
  return z.preprocess(emptyToUndefined, z.string().max(max, message).optional());
}

function optionalTrimmedPattern(regex: RegExp, message: string, max = 255) {
  return z.preprocess(
    normalizeDigitsAndSpaces,
    z.string().max(max).regex(regex, message).optional(),
  );
}

// ── Enums ─────────────────────────────────────────────────

export const LoanTypeEnum = z.enum(['PERSONAL', 'BUSINESS', 'MORTGAGE', 'AUTO', 'LINE_OF_CREDIT']);

export const PaymentFrequencyEnum = z.enum(['WEEKLY', 'BIWEEKLY', 'MONTHLY', 'QUARTERLY']);

export const PaymentMethodEnum = z.enum(['CASH', 'BANK_TRANSFER', 'CHECK', 'CARD', 'DIRECT_DEBIT']);

export const LoanStatusEnum = z.enum([
  'APPLICATION',
  'UNDER_REVIEW',
  'APPROVED',
  'DISBURSED',
  'CURRENT',
  'DELINQUENT_30',
  'DELINQUENT_60',
  'DELINQUENT_90',
  'DEFAULT',
  'PAID_OFF',
  'RESTRUCTURED',
  'CANCELLED',
]);

export const BorrowerEntityTypeEnum = z.enum([
  'INDIVIDUAL',
  'SOLE_TRADER',
  'PARTNERSHIP',
  'COMPANY',
  'TRUST',
  'SMSF',
  'ASSOCIATION',
  'GOVERNMENT',
  'JOINT_VENTURE',
  'OTHER',
]);

export const GuarantorEntityTypeEnum = z.enum(['INDIVIDUAL', 'COMPANY', 'TRUST']);

export const GuaranteeTypeEnum = z.enum([
  'PERSONAL',
  'CORPORATE',
  'DIRECTOR',
  'THIRD_PARTY',
  'LIMITED_RECOURSE',
]);

// ── Loan Schemas ──────────────────────────────────────────

export const createLoanSchema = z.object({
  borrowerId: z.string().uuid('Please select a borrower'),
  loanType: LoanTypeEnum,
  principal: z.coerce
    .number({ message: 'Principal is required' })
    .min(100, 'Principal must be at least $100')
    .max(100_000_000, 'Principal cannot exceed $100,000,000'),
  interestRate: z.coerce
    .number({ message: 'Interest rate is required' })
    .min(0, 'Interest rate cannot be negative')
    .max(100, 'Interest rate cannot exceed 100%'),
  termMonths: z.coerce
    .number({ message: 'Term is required' })
    .int('Term must be a whole number')
    .min(1, 'Term must be at least 1 month')
    .max(360, 'Term cannot exceed 360 months'),
  paymentFrequency: PaymentFrequencyEnum.default('MONTHLY'),
  defaultRate: z.coerce.number().min(0).max(100).optional().nullable(),
  establishmentFee: optionalMonetary,
  brokerageFee: optionalMonetary,
  legalCosts: optionalMonetary,
  otherCosts: optionalMonetary,
  otherCostsDescription: z.string().max(500).optional(),
  referrerId: z.string().uuid().optional().or(z.literal('')),
  fundId: z.string().uuid().optional().or(z.literal('')),
  interestMethod: z.enum(['PERCENTAGE', 'FLAT_AMOUNT']).default('PERCENTAGE'),
  flatInterestAmount: z.coerce.number().nonnegative().optional().nullable(),
  settlementDate: dateOnlyField.optional().nullable().or(z.literal('')),
});

export type CreateLoanInput = z.infer<typeof createLoanSchema>;

export const updateLoanSchema = createLoanSchema.partial().extend({
  settlementDate: dateOnlyField.optional().nullable().or(z.literal('')),
  internalReference: z.string().max(100).optional().nullable(),
  lawFirmReference: z.string().max(100).optional().nullable(),
  externalReference: z.string().max(100).optional().nullable(),
});

export type UpdateLoanInput = z.infer<typeof updateLoanSchema>;

// ── Borrower Schemas ──────────────────────────────────────

const borrowerAddressSchema = z
  .object({
    street: optionalTrimmedString(255),
    city: optionalTrimmedString(120),
    suburb: optionalTrimmedString(120),
    state: optionalTrimmedString(120),
    postcode: optionalTrimmedPattern(AUS_POSTCODE_REGEX, 'Invalid postcode format, e.g. 2000', 12),
    country: z.preprocess(emptyToUndefined, z.string().max(2).default('AU')).optional(),
  })
  .optional();

const directorSchema = z.object({
  name: z.preprocess(emptyToUndefined, z.string().min(1, 'Director name is required').max(200)),
  role: z.preprocess(emptyToUndefined, z.string().min(1, 'Director role is required').max(100)),
  dateAppointed: optionalTrimmedString(50),
});

const borrowerObjectSchema = z.object({
  name: z.preprocess(
    emptyToUndefined,
    z
      .string()
      .min(2, 'Name must be at least 2 characters')
      .max(200, 'Name must be at most 200 characters'),
  ),
  email: z.preprocess(emptyToUndefined, emailField.optional()),
  phone: z.preprocess(emptyToUndefined, phoneField.optional()),
  taxId: optionalTrimmedString(50),
  entityType: BorrowerEntityTypeEnum.default('INDIVIDUAL'),
  abn: optionalTrimmedPattern(AUS_ABN, 'Incorrect ABN, example of correct one: 12345678901', 20),
  acn: optionalTrimmedPattern(AUS_ACN, 'Incorrect ACN, example of correct one: 123456789', 20),
  abnStatus: optionalTrimmedString(100),
  entityName: optionalTrimmedString(200),
  gstRegistered: z.boolean().default(false),
  trustType: optionalTrimmedString(100),
  trusteeType: optionalTrimmedString(100),
  trusteeName: optionalTrimmedString(200),
  directors: z.preprocess(
    value => (Array.isArray(value) ? value.filter(Boolean) : value),
    z.array(directorSchema).max(20, 'A maximum of 20 directors is allowed').optional(),
  ),
  address: borrowerAddressSchema,
});

function addBorrowerRules(schema: z.ZodObject<any>) {
  return schema.superRefine((data: any, ctx: z.RefinementCtx) => {
    if (data.entityType === 'TRUST' && data.trusteeType && !data.trusteeName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['trusteeName'],
        message: 'Trustee name is required',
      });
    }
  });
}

export const createBorrowerSchema = addBorrowerRules(borrowerObjectSchema);

export const updateBorrowerSchema = addBorrowerRules(borrowerObjectSchema.partial());

export type CreateBorrowerInput = z.infer<typeof createBorrowerSchema>;
export type UpdateBorrowerInput = z.infer<typeof updateBorrowerSchema>;

// ── Guarantor Schemas ─────────────────────────────────────

const optionalIsoDate = z.preprocess(
  emptyToUndefined,
  z
    .string()
    .date('Please enter a valid date')
    .transform(value => new Date(`${value}T00:00:00.000Z`).toISOString())
    .optional(),
);

export const createGuarantorSchema = z
  .object({
    loanId: z.string().uuid('Loan is required'),
    name: z.preprocess(
      emptyToUndefined,
      z
        .string()
        .min(2, 'Name must be at least 2 characters')
        .max(200, 'Name must be at most 200 characters'),
    ),
    entityType: GuarantorEntityTypeEnum.default('INDIVIDUAL'),
    abn: optionalTrimmedPattern(AUS_ABN, 'Incorrect ABN, example of correct one: 12345678901', 20),
    email: z.preprocess(emptyToUndefined, emailField.optional()),
    phone: z.preprocess(emptyToUndefined, phoneField.optional()),
    guaranteeType: GuaranteeTypeEnum.default('PERSONAL'),
    limitOfLiability: z.preprocess(
      emptyToUndefined,
      z.coerce
        .number({ message: 'Limit of Liability is required' })
        .positive('Limit of Liability must be greater than $0')
        .max(100_000_000, 'Limit of Liability cannot exceed $100,000,000'),
    ),
    guaranteeDate: optionalIsoDate,
    expiryDate: optionalIsoDate,
    notes: optionalTrimmedString(1000, 'Notes must be at most 1000 characters'),
  })
  .superRefine((data, ctx) => {
    if ((data.entityType === 'COMPANY' || data.entityType === 'TRUST') && !data.abn) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['abn'],
        message: 'ABN is required for company and trust guarantors',
      });
    }

    if (data.guaranteeDate && data.expiryDate && data.expiryDate < data.guaranteeDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['expiryDate'],
        message: 'Expiry date must be on or after the guarantee date',
      });
    }
  });

export type CreateGuarantorInput = z.infer<typeof createGuarantorSchema>;

// ── Payment Schemas ───────────────────────────────────────

export const recordPaymentSchema = z.object({
  loanId: z.string().uuid('Loan is required'),
  amount: monetaryAmount.refine(v => v >= 0.01, 'Payment must be at least $0.01'),
  method: PaymentMethodEnum,
  reference: z.string().max(100).optional(),
  notes: z.string().max(1000).optional(),
  receivedDate: z.string().optional(),
  processedBy: z.string().uuid().optional(),
});

export type RecordPaymentInput = z.infer<typeof recordPaymentSchema>;

export const reversePaymentSchema = z.object({
  paymentId: z.string().uuid('Payment ID is required'),
  reason: z.string().min(5, 'Reason must be at least 5 characters').max(500),
});

export type ReversePaymentInput = z.infer<typeof reversePaymentSchema>;

// ── Auth Schemas ──────────────────────────────────────────

export const loginSchema = z.object({
  email: emailField,
  password: z.string().min(1, 'Password is required'),
});

export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = z
  .object({
    email: emailField,
    password: z
      .string()
      .min(10, 'Password must be at least 10 characters')
      .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Must contain at least one number')
      .regex(specialCharRegex, 'Must contain at least one special character'),
    confirmPassword: z.string(),
    firstName: z.string().min(1, 'First name is required').max(100),
    lastName: z.string().min(1, 'Last name is required').max(100),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type RegisterInput = z.infer<typeof registerSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(10, 'Password must be at least 10 characters')
      .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Must contain at least one number')
      .regex(specialCharRegex, 'Must contain at least one special character'),
    confirmPassword: z.string(),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine(data => data.currentPassword !== data.newPassword, {
    message: 'New password must differ from current password',
    path: ['newPassword'],
  });

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// ── Helpers ───────────────────────────────────────────────

/**
 * Validate form data against a Zod schema, returning a flat
 * field → error-message map compatible with the existing
 * `ValidationResult` type from `lib/validation.ts`.
 */
export function validateWithSchema<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
): { success: true; data: T } | { success: false; errors: Record<string, string> } {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }
  const errors: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join('.');
    if (!errors[key]) {
      errors[key] = issue.message;
    }
  }
  return { success: false, errors };
}
